import type { SetStoreFunction } from "solid-js/store";
import { reconcile } from "solid-js/store";

import { getUserInfoApi } from "@/api/clientApi/user";
import { GetPutTypesV4, RemovePutInfo } from "@/api/clientApi/wallet";
import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog";
import { PutGroupEnum } from "@/enum/wallet.ts";
import { useI18n } from "@/i18n";
import { isMobile } from "@/lib/isMobile";
import { sdkManager } from "@/lib/sdk/sdkManager";
import { getAppType, getOS } from "@/lib/utils";
import {
  isCustomRegular,
  isPositiveInteger,
  isRealName,
  isValidCpf,
} from "@/lib/validateUtils";
import { userStore } from "@/stores/userStore";
import {
  type FiatFormProps,
  type WithdrawFromInfo,
  WithdrawInfoField,
  bankCardStore,
  bankFromStore,
  faitFromStore,
  getBindPutInfo,
  setBankCardStore,
  setBankFromStore,
  setEnterDialogList,
  setFaitFrom,
  setFaitFromStore,
  setPixBankFrom,
  setWithdrawStore,
  withdrawAvailable,
  withdrawStore,
} from "@/stores/wallet/withdrawStore";
import {
  WithdrawBindInfo,
  WithdrawLimitCfg,
} from "@/types/swagger/userwealth.ts";

interface WithdrawStatusResult {
  disabledStatus: boolean;
  payDisabled: boolean;
  kycDisabled: boolean;
  cpfDisabled: boolean;
  accountDisabled: boolean;
  textList: string[];
}
interface VerifyStatusResult {
  status: boolean;
  textList: string[];
}
export function useWithdraw() {
  const { t } = useI18n();
  const { showDialog, closeDialog } = useDialog();

  // 提现-法币-刷新用户信息
  const refreshMoney = () => {
    if (withdrawStore.refreshMoney) {
      return;
    }
    setWithdrawStore("refreshMoney", true);
    getUserInfoApi().finally(() => {
      setTimeout(() => {
        setWithdrawStore("refreshMoney", false);
      }, 300);
    });
  };
  // 提现-法币-统计金额
  // const feeInfo = createMemo(() => {
  //   const putTypeCfg = getPutTypeCfg();
  //   const value = Number(withdrawStore.withdrawFromInfo.point) || 0;
  //   let fee = 0;
  //   let realMoney = 0;
  //   let str = "";
  //   const fee_percent = putTypeCfg?.fee_percent || 0;
  //   const fee_fixed_point = putTypeCfg?.fee_fixed_point || 0;
  //   const fee_percent_min_point = putTypeCfg?.fee_percent_min_point || 0;
  //   if (value) {
  //     if (fee_percent) {
  //       fee += (fee_percent / 100) * value;
  //       if (fee_percent_min_point) {
  //         fee = Math.max(fee_percent_min_point, fee);
  //       }
  //     }
  //     if (fee_fixed_point) {
  //       fee += fee_fixed_point;
  //     }
  //   }
  //   if (fee_percent && fee_fixed_point) {
  //     str = `(${fee_percent}% + ${fee_fixed_point} ${putTypeCfg?.point_name})`;
  //   } else if (fee_percent) {
  //     str = `(${fee_percent}%)`;
  //   } else if (fee_fixed_point) {
  //     str = `(${fee_fixed_point} ${putTypeCfg?.point_name})`;
  //   }
  //   if (value) {
  //     const money = value - Number(fee);
  //     realMoney = money > 0 ? money : 0;
  //   }
  //   return {
  //     fee,
  //     realMoney,
  //     str,
  //     fee_percent,
  //     fee_fixed_point,
  //     fee_percent_min_point,
  //   };
  // });
  // 验证表单全部内容
  const isValidateWithdrawForm = (
    fromStore: FiatFormProps["fromStore"],
    setFromStore: FiatFormProps["setFromStore"],
  ): boolean => {
    let error: boolean = true;
    Object.values(fromStore).forEach((item) => {
      validateWithdrawForm(
        item.name,
        item.value,
        setFromStore,
        item.regex || "",
      );
      if (item.error) {
        error = false;
      }
    });
    return error;
  };
  // 提现-表单验证
  const validateWithdrawForm = (
    field: WithdrawInfoField,
    value: string,
    setStore: SetStoreFunction<Record<string, WithdrawFromInfo>>,
    regex: string,
  ) => {
    let error: string = "";
    switch (field) {
      case "cpf":
        if (!value || !isValidCpf(value)) {
          error = t("withdraw.error_cpf");
          sdkManager.tdReportEvent("withdraw_cpf_check_fail", {});
        } else {
          sdkManager.tdReportEvent("withdraw_cpf_check_success", {});
        }
        break;
      case "fullName":
        if (!value.trim() || !isRealName(value)) {
          error = t("withdraw.error_name");
          sdkManager.tdReportEvent(
            "withdraw_account_holder_name_check_fail",
            {},
          );
        } else {
          sdkManager.tdReportEvent(
            "withdraw_account_holder_name_check_success",
            {},
          );
        }
        break;
      case "bankName":
        if (!value) {
          error = t("withdraw.error_bank_name");
          sdkManager.tdReportEvent("withdraw_bank_name_check_fail", {});
        } else {
          sdkManager.tdReportEvent("withdraw_bank_name_check_success", {});
        }
        break;
      case "accountType":
        if (!value) {
          error = t("withdraw.error_bank_type");
          sdkManager.tdReportEvent("withdraw_account_type_check_fail", {});
        } else {
          sdkManager.tdReportEvent("withdraw_account_type_check_success", {});
        }
        break;
      case "branchNumber":
        if (!value.trim() || (regex && !isCustomRegular(value, regex))) {
          error = t("withdraw.error_number");
          sdkManager.tdReportEvent("withdraw_institution_code_check_fail", {});
        } else {
          sdkManager.tdReportEvent(
            "withdraw_institution_code_check_success",
            {},
          );
        }
        break;
      case "accountNumber":
        if (!value.trim() || (regex && !isCustomRegular(value, regex))) {
          error = t("withdraw.error_number");
          sdkManager.tdReportEvent("withdraw_account_check_fail", {});
        } else {
          sdkManager.tdReportEvent("withdraw_account_check_success", {});
        }
        break;
      case "point":
        if (!value.trim() || isPositiveInteger(value)) {
          error = t("withdraw.error_amount");
          sdkManager.tdReportEvent("withdraw_amount_check_fail", {});
        }
        if (Number(value) > Number(withdrawAvailable())) {
          error = t("withdraw.error_insufficient");
        }
        if (!error) {
          sdkManager.tdReportEvent("withdraw_amount_check_success", {});
        }
        break;
      default:
        break;
    }
    setStore(field, "error", error);
  };

  // 提现-二级弹窗
  const handleClickWithdraw = () => {
    if (isValidateWithdrawForm(faitFromStore, setFaitFromStore)) {
      setWithdrawStore("isWithdrawLoading", true);
      setEnterDialogList();
      showDialog({
        type: DialogType.WithdrawEnter,
      });
      setTimeout(() => {
        setWithdrawStore("isWithdrawLoading", false);
      }, 1000);
      sdkManager.tdReportEvent("withdraw_submit", {});
    }
  };
  // 提现-是否热标签
  const isHotTag = () => {
    return (
      withdrawStore.fait_list[Object.keys(withdrawStore.fait_list)[0]]?.list[0]
        .id == withdrawStore.currentPayInfo.id
    );
  };
  // 获取绑定银行卡号
  const getBankCardInfo = async () => {
    const res = await GetPutTypesV4({ group_id: PutGroupEnum.bankCard });
    if (res) {
      const list: WithdrawBindInfo[] = [];
      if (res?.bind_info) {
        const bindInfo = res.bind_info as Record<string, WithdrawBindInfo[]>;
        for (const key in bindInfo) {
          const cardList = bindInfo?.[key]?.filter((item) => {
            if (item.group_id == PutGroupEnum.bankCard) {
              item.entrus_bank_name = `${item.entrus_bank_name} (${res?.bank_account_type_cfg?.[item?.account_type ?? 0].name ?? ""})`;
              return true;
            }
            return false;
          });
          list.push(...cardList);
        }
      }
      setBankCardStore("bindBankCardList", reconcile(list));
    }
    return res;
  };
  // 删除银行卡号
  const deleteBankCard = async () => {
    await RemovePutInfo({ put_type: 1, entrus_bank_account: "2" }).then(() => {
      getBankCardInfo();
    });
  };
  // 切换绑定卡号
  const onChangeBank = (index: number) => {
    setWithdrawStore(
      "chooseBankCard",
      reconcile(bankCardStore.bindBankCardList[index]),
    );
    closeDialog(DialogType.BankCard);
    setFaitFrom(PutGroupEnum.bankCard);
    setPixBankFrom();
  };
  // 添加银行卡
  const onBankCardAdd = () => {
    if (isValidateWithdrawForm(bankFromStore, setBankFromStore)) {
      getBindPutInfo(true);
    }
  };
  // 1 邮箱验证 2 电话验证 3 邮箱或者电话验证 4 邮箱且电话验证
  const getVerifyStatusByType = (verifyType: number): VerifyStatusResult => {
    const result: VerifyStatusResult = { status: true, textList: [] };
    const emailBind = userStore.userInfo?.email_bind ?? false;
    const phoneBind = userStore.userInfo?.phone_bind ?? false;
    switch (verifyType) {
      case 1:
        result.status = emailBind;
        result.textList = [t("settings.email_verification")];
        break;
      case 2:
        result.status = phoneBind;
        result.textList = [t("settings.phoneverification")];
        break;
      case 3:
        result.status = emailBind || phoneBind;
        result.textList = [
          `${t("settings.email_verification")}/${t("settings.phoneverification")}`,
        ];
        break;
      case 4:
        result.status = emailBind && phoneBind;
        result.textList = [];
        if (!emailBind) {
          result.textList.push(t("settings.email_verification"));
        }
        if (!phoneBind) {
          result.textList.push(t("settings.phoneverification"));
        }
        break;
    }
    return result;
  };
  // 提现状态
  const withdrawStatus = (): WithdrawStatusResult => {
    const result: WithdrawStatusResult = {
      disabledStatus: false,
      payDisabled: false,
      kycDisabled: false,
      cpfDisabled: false,
      accountDisabled: false,
      textList: [],
    };
    const withdrawLimitCfg: WithdrawLimitCfg = withdrawStore.withdrawLimitCfg;

    if (withdrawLimitCfg?.pay) {
      if (!userStore.userInfo?.is_pay) {
        result.disabledStatus = true;
        result.payDisabled = true;
      }
    }
    // if (withdrawLimitCfg?.kyc) {
    //   if (withdrawStore.kycStatus != kycVerifyStatusResult.success) {
    //     result.disabledStatus = true;
    //     result.kycDisabled = true;
    //   }
    // }
    // if (withdrawLimitCfg?.cpf) {
    //   result.disabledStatus = true;
    //   result.cpfDisabled = true;
    // }
    const accountObj = getVerifyStatusByType(withdrawLimitCfg?.account_verify);
    if (!accountObj.status) {
      result.disabledStatus = true;
      result.accountDisabled = true;
      result.textList = accountObj.textList;
    }
    return result;
  };
  // 处理提现限制
  const handleWithdrawLimit = (push: (path: string) => void) => {
    if (withdrawStatus().disabledStatus) {
      let content = "";
      let confirmText = t("account.settings");
      if (withdrawStatus().payDisabled) {
        confirmText = t("common.deposit");
        content = `<p>● ${t("tips.no_deposit_content")}</p>`;
      } else {
        if (
          withdrawStatus().accountDisabled &&
          withdrawStatus().textList?.length
        ) {
          withdrawStatus().textList?.forEach((el: string) => {
            content += `<p>● ${el}</p>`;
          });
        }
        // if (withdrawStatus().kycDisabled) {
        //   content += `<p>● ${t("settings.verify_prompt")}</p>`;
        //   confirmText = t("common.verify");
        // }
        // if (withdrawStatus().cpfDisabled) {
        //   content += `<p>● ${t("common.cpf_ver")}</p>`;
        // }
      }
      showDialog({
        type: DialogType.Tips,
        data: {
          title: t("tips.tips"),
          content: content,
          confirmText: confirmText,
          onConfirm: () => {
            closeDialog(DialogType.WithdrawEnter);
            if (withdrawStatus().payDisabled) {
              push("/deposit");
            } else {
              if (withdrawStatus().accountDisabled) {
                push("/settings?tab=security");
              } else {
                push("/settings?tab=verify");
              }
            }
          },
          cancelText: t("common.cancel"),
        },
      });
      return true;
    }
    return false;
  };
  // 确认提现
  const onEnterWithdraw = (push: (path: string) => void) => {
    if (handleWithdrawLimit(push)) {
      return;
    }
    if (isMobile()) {
      getBindPutInfo();
    } else {
      getBindPutInfo();
    }
  };
  // 人脸识别
  const onFacialRecognition = () => {};
  // 银行卡界面点击显示添加弹窗
  const onShowAddBankCardPopup = () => {
    // initWithdrawStore(true).then(() => {
    showDialog({
      type: DialogType.BankCardAdd,
    });
    // });
  };
  // 打开银行卡列表弹窗
  const onOpenBankCard = () => {
    showDialog({
      type: DialogType.BankCard,
      data: { isH5Popup: true },
    });
  };
  return {
    refreshMoney,
    validateWithdrawForm,
    handleClickWithdraw,
    isHotTag,
    onChangeBank,
    getBankCardInfo,
    deleteBankCard,
    onBankCardAdd,
    onFacialRecognition,
    onEnterWithdraw,
    isValidateWithdrawForm,
    onShowAddBankCardPopup,
    onOpenBankCard,
  };
}
